import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import MiniMap from '../components/MiniMap';
import { MapPin, Phone, Mail, Eye, Home, Building, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://silver-chicken-493994.hostingersite.com';

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inquiryData, setInquiryData] = useState({
    buyer_name: '',
    buyer_email: '',
    buyer_phone: '',
    message: '',
    inquiry_type: 'general'
  });
  const [sendingInquiry, setSendingInquiry] = useState(false);

  useEffect(() => {
    fetchProperty();
  }, [id]);

  const fetchProperty = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/properties/${id}`);
      setProperty(response.data);
    } catch (error) {
      console.error('Error fetching property:', error);
      toast.error('Property not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleInquiryChange = (e) => {
    setInquiryData({ ...inquiryData, [e.target.name]: e.target.value });
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setSendingInquiry(true);
    
    try {
      await axios.post(`${API_URL}/api/inquiries`, {
        property_id: property.id,
        ...inquiryData
      });
      toast.success('Inquiry sent! The mediator will contact you soon.');
      setInquiryData({
        buyer_name: '',
        buyer_email: '',
        buyer_phone: '',
        message: '',
        inquiry_type: 'general'
      });
    } catch (error) {
      console.error('Error sending inquiry:', error);
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setSendingInquiry(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading property details...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800">Property not found</h2>
          <button 
            onClick={() => navigate('/')}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Property Details */}
        <div className="lg:col-span-2">
          <button 
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:text-blue-800 mb-4 inline-block"
          >
            ← Back
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{property.title}</h1>
          <p className="text-gray-600 mb-4">{property.district}, {property.city}, {property.state}</p>
          
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            {property.images && property.images.length > 0 && (
              <div className="grid grid-cols-2 gap-2 p-4">
                <img 
                  src={property.images[0]?.image_url} 
                  alt={property.title}
                  className="col-span-2 w-full h-64 object-cover rounded-lg"
                />
                {property.images.slice(1, 4).map((img) => (
                  <img 
                    key={img.id}
                    src={img.image_url}
                    alt={property.title}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-blue-600 mb-4">
              ₹{Number(property.selling_price).toLocaleString()}
            </h2>
            <p className="text-sm text-gray-500 mb-2">
              Commission: {property.commission_percent}% from both parties
            </p>
            <div className="border-t pt-4">
              <p className="text-gray-700 whitespace-pre-line">{property.description}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h3 className="text-lg font-semibold mb-3">Property Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Type</p>
                <p className="font-medium capitalize">{property.property_type}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className="font-medium capitalize">{property.status}</p>
              </div>
              {property.land_area && (
                <div>
                  <p className="text-sm text-gray-500">Land Area</p>
                  <p className="font-medium">{property.land_area} sq.ft</p>
                </div>
              )}
              {property.bedrooms > 0 && (
                <div>
                  <p className="text-sm text-gray-500">Bedrooms</p>
                  <p className="font-medium">{property.bedrooms}</p>
                </div>
              )}
              {property.bathrooms > 0 && (
                <div>
                  <p className="text-sm text-gray-500">Bathrooms</p>
                  <p className="font-medium">{property.bathrooms}</p>
                </div>
              )}
              {property.facing && (
                <div>
                  <p className="text-sm text-gray-500">Facing</p>
                  <p className="font-medium">{property.facing}</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-3">Neighbor Details</h3>
            <div className="grid grid-cols-2 gap-4">
              {property.north_neighbor && (
                <div><p className="text-sm text-gray-500">North</p><p className="font-medium">{property.north_neighbor}</p></div>
              )}
              {property.south_neighbor && (
                <div><p className="text-sm text-gray-500">South</p><p className="font-medium">{property.south_neighbor}</p></div>
              )}
              {property.east_neighbor && (
                <div><p className="text-sm text-gray-500">East</p><p className="font-medium">{property.east_neighbor}</p></div>
              )}
              {property.west_neighbor && (
                <div><p className="text-sm text-gray-500">West</p><p className="font-medium">{property.west_neighbor}</p></div>
              )}
            </div>
          </div>
        </div>

        {/* Location Map Section */}
<div className="bg-white rounded-lg shadow-lg p-6 mb-6">
  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
    <MapPin className="w-5 h-5 text-primary-500" />
    Property Location
  </h3>
  <MiniMap 
    latitude={property.latitude} 
    longitude={property.longitude}
    title={property.title}
  />
  <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
    <div>
      <p className="text-neutral-500">District</p>
      <p className="font-medium">{property.district}</p>
    </div>
    <div>
      <p className="text-neutral-500">City</p>
      <p className="font-medium">{property.city}</p>
    </div>
    {property.village && (
      <div>
        <p className="text-neutral-500">Village</p>
        <p className="font-medium">{property.village}</p>
      </div>
    )}
    <div>
      <p className="text-neutral-500">Address</p>
      <p className="font-medium text-sm">{property.address}</p>
    </div>
  </div>
</div>

        {/* Right: Contact & Inquiry - Shows Mediator Name + Client Contact */}
<div className="lg:col-span-1">
  <div className="bg-white rounded-lg shadow-lg p-6 sticky top-4">
    <h3 className="text-lg font-semibold mb-4">📞 Contact Nalla Brokers</h3>
    
    {/* ==========================================
        LISTED BY - SHOWS ACTUAL MEDIATOR NAME
        ========================================== */}
    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
      <p className="text-xs text-gray-500">Property Listed By</p>
      <p className="font-semibold text-gray-700">
        {property.mediator_name || 'Nalla Brokers'}
      </p>
      {property.mediator_company && (
        <p className="text-sm text-gray-600">{property.mediator_company}</p>
      )}
    </div>

    {/* ==========================================
        CLIENT CONTACT - ALWAYS CLIENT'S NUMBER
        ========================================== */}
    <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
      <p className="text-sm font-medium text-blue-700 mb-2">
        📞 Contact Nalla Brokers
      </p>
      <p className="text-xs text-gray-500 mb-3">
        Our team will connect you with the property owner/mediator
      </p>
      <div className="flex flex-col gap-2">
        <a 
          href="tel:+919159904385"
          className="bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 w-full text-center font-medium transition-all"
        >
          📞 Call Now: +91 9159904385
        </a>
        <a 
          href="mailto:karikalank@gmail.com"
          className="bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 w-full text-center font-medium transition-all"
        >
          ✉️ Send Email
        </a>
      </div>
    </div>

    {/* ==========================================
        INQUIRY FORM - SENT TO CLIENT
        ========================================== */}
    <form onSubmit={handleInquirySubmit} className="space-y-3">
      <p className="text-xs text-gray-500 mb-2">
        Or send us a message and we'll get back to you
      </p>
      
      <input
        type="text"
        name="buyer_name"
        placeholder="Your Name *"
        required
        className="w-full p-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        value={inquiryData.buyer_name}
        onChange={handleInquiryChange}
      />
      <input
        type="tel"
        name="buyer_phone"
        placeholder="Your Phone *"
        required
        className="w-full p-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        value={inquiryData.buyer_phone}
        onChange={handleInquiryChange}
      />
      <input
        type="email"
        name="buyer_email"
        placeholder="Your Email"
        className="w-full p-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        value={inquiryData.buyer_email}
        onChange={handleInquiryChange}
      />
      <select
        name="inquiry_type"
        className="w-full p-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        value={inquiryData.inquiry_type}
        onChange={handleInquiryChange}
      >
        <option value="general">General Inquiry</option>
        <option value="site_visit">Schedule Site Visit</option>
        <option value="document_request">Request Documents</option>
        <option value="price_negotiation">Price Negotiation</option>
      </select>
      <textarea
        name="message"
        placeholder="Your Message"
        rows="3"
        className="w-full p-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        value={inquiryData.message}
        onChange={handleInquiryChange}
      />
      <button
        type="submit"
        disabled={sendingInquiry}
        className="w-full bg-primary-500 text-white py-2 rounded-lg hover:bg-primary-600 disabled:opacity-50 font-medium transition-all"
      >
        {sendingInquiry ? 'Sending...' : 'Send Inquiry'}
      </button>
      
      <p className="text-xs text-gray-400 text-center mt-2">
        All inquiries are handled by Nalla Brokers
      </p>
    </form>
  </div>
</div>
      </div>
    </div>
  );
};

export default PropertyDetail;